import { Badge } from "@/components/ui/Badge";
import { Card, CardTitle } from "@/components/ui/Card";
import { ProviderIcon } from "@/components/ui/ProviderIcon";
import { statusToBadgeVariantRaw as statusToBadgeVariant } from "@/lib/constants";
import { cn } from "@/lib/utils";
import * as shared from "@/styles/shared";
import type { AddressCardProps } from "@/types/invoices";
import { getAfterPaymentStatus } from "@shared/constants/providers";
import type { AddressProvider } from "@shared/types/models";
import { ChevronDown, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { styles } from "../AddressesPage.styles";
import { useAddresses } from "./AddressesContext";

const AddressCard = ({ address }: AddressCardProps) => {
  const ctx = useAddresses();
  const isExpanded = ctx.expandedId === address.id;
  const toggle = () => ctx.setExpandedId(isExpanded ? null : address.id);

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (["Enter", " "].includes(e.key)) {
      e.preventDefault();
      toggle();
    }
  };
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.openEdit(address);
  };
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.handleDelete(address.id);
  };
  const handleAssignClick = () => ctx.openAssign(address.id);
  const handleEditProviderClick = (ap: AddressProvider) => ctx.openEditProvider(ap);
  const handleRemoveProviderClick = (ap: AddressProvider) => ctx.handleRemoveProvider(address.id, ap.providerId);

  return (
    <Card key={address.id} className={styles.card} variant="elevated">
      <div className={styles.cardHeader} onClick={toggle} role="button" tabIndex={0} aria-expanded={isExpanded} onKeyDown={handleHeaderKeyDown}>
        <div className={styles.cardHeaderContent}>
          <CardTitle className={styles.cardTitle}>
            <MapPin className={styles.cardTitleIcon} />
            {address.name}
          </CardTitle>
          <p className={shared.muted}>{ctx.t("addresses:providersCount", { count: address.providers?.length || 0 })}</p>
        </div>
        <div className={shared.rowGap1}>
          <span className={styles.expandLabel} aria-hidden="true">
            <ChevronDown className={cn(styles.iconH4W4Transition, isExpanded && "rotate-180")} />
          </span>
          <button onClick={handleEditClick} className={shared.iconButton} title={ctx.t("button.edit")}>
            <Pencil className={shared.iconSm} />
          </button>
          <button onClick={handleDeleteClick} className={styles.iconButtonDanger} title={ctx.t("button.delete")}>
            <Trash2 className={shared.iconSm} />
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className={styles.expandedSection}>
          <button onClick={handleAssignClick} className={styles.assignButton}>
            <Plus className={shared.iconSm} />
            {ctx.t("addresses:assignProvider")}
          </button>
          {address.providers?.length ? (
            <div className={styles.providerList}>
              {address.providers.map((ap) => {
                const status = getAfterPaymentStatus(ap.provider.name);
                return (
                  <div key={ap.id} className={styles.providerItem}>
                    <span className={styles.providerName}>
                      <ProviderIcon provider={ap.provider.name} size="md" />
                      {ap.provider.name}
                    </span>
                    <div className={styles.providerActions}>
                      <Badge variant={statusToBadgeVariant(status)} size="sm">
                        {ctx.t(status, { ns: "status" })}
                      </Badge>
                      <button onClick={() => handleEditProviderClick(ap)} className={shared.iconButton} title={ctx.t("button.edit")}>
                        <Pencil className={shared.iconXs} />
                      </button>
                      <button
                        onClick={() => handleRemoveProviderClick(ap)}
                        className={styles.iconButtonDanger}
                        title={ctx.t("addresses:removeProvider")}
                      >
                        <Trash2 className={shared.iconXs} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
};

export { AddressCard };
